// eslint-disable @typescript-eslint/no-explicit-any
import {
  Box,
  Center,
  Flex,
  Grid,
  HStack,
  IconButton,
  Input,
  Separator,
  SimpleGrid,
  Skeleton,
  SkeletonCircle,
  SkeletonText,
  Spacer,
  Spinner,
  useToken,
  VStack,
} from "@chakra-ui/react";
import React, { use, useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FaEye } from "react-icons/fa";
import { FaCartShopping } from "react-icons/fa6";
import { HiOutlineDownload, HiOutlineEye, HiPaperClip, HiX } from "react-icons/hi";
import { HiOutlineClipboardDocumentCheck, HiOutlineDocumentCurrencyDollar } from "react-icons/hi2";
import { MdAdd } from "react-icons/md";
import { useInfiniteQuery } from "react-query";
import { useNavigate } from "react-router";
import CalendarIcon from "@/assets/icons/CalendarIcon";
import InvoiceIcon from "@/assets/icons/InvoiceIcon";
import OrdersIcon from "@/assets/icons/OrdersIcon";
import FaceliftAvatarNoBadge from "@/components/Atoms/FaceliftAvatarNoBadge";
import FaceliftButton from "@/components/Atoms/FaceliftButton";
import FaceliftDataLoader from "@/components/Atoms/FaceliftDataLoader";
import FaceliftIconButton from "@/components/Atoms/FaceliftIconButton";
import FaceliftInput from "@/components/Atoms/FaceliftInput";
import FaceliftSelect from "@/components/Atoms/FaceliftSelect/FaceliftSelect";
import type { SelectItem } from "@/components/Atoms/FaceliftSelect/FaceliftSelect.types";
import FaceliftText from "@/components/Atoms/FaceliftText";
import type { DateRange } from "@/components/Molecules/FaceliftDatePicker";
import FaceliftDialogWithExitConfirmation from "@/components/Molecules/FaceliftDialogWithExitConfirmation";
import FaceliftInvoiceViewPDF from "@/components/Molecules/FaceliftInvoiceViewPDF";
import Panel from "@/components/Molecules/Panel";
import ListPanel from "@/components/Molecules/Panel/ListPanel";
import { ScoreCardDateFilter } from "@/components/Molecules/ScoreCardDateFilter";
import type { DateFilterState } from "@/components/Molecules/ScoreCardDateFilter/ScoreCardDateFilter";
import Show from "@/components/Molecules/Show";
import BenefitVerificationForm, {
} from '@/components/Templates/BenefitVerification/BenefitVerificationForm';
import {
  bvStatusMap,
  getBVStatus,
  getBvStatuses,
} from "@/components/Templates/BenefitVerification/BenefitVerificationTable/bv";
import Preview from "@/components/Templates/BenefitVerification/Steps/Preview";
import { BulkConsignmentOrderForm } from "@/components/Templates/ConsignmentOrders";
import DetailsView from "@/components/Templates/DetailsView";
import {
  getInvoiceStatusFeed,
  statusInvoicesFeed,
} from "@/components/Templates/Invoices/invoices";
import { OrderRequestForm } from "@/components/Templates/Requests";
import { getRequestStatus } from "@/components/Templates/Requests/RequestsTable";
import { parseOrderDetailsForModal } from "@/components/Templates/Requests/utils";
import { SupportTicketForm } from "@/components/Templates/SupportTickets";
import { colors } from "@/components/ui/theme";
import { Tooltip } from "@/components/ui/tooltip";
import { useInvoicePDFViewer } from "@/hooks";
import { useGetAccounts } from "@/hooks/service-hooks/accounts";
import {
  useGetRecentBVCount,
  useGetSingleBV,
} from "@/hooks/service-hooks/benefitsVerification";
import { useGetActivityFile, useUploadToActivity } from "@/hooks/service-hooks/common";
import {
  useGetOverDueAmount,
} from "@/hooks/service-hooks/invoice";
import { useGetOrderById } from "@/hooks/service-hooks/requests";
import { useInvoiceCart } from "@/hooks/useInvoiceCart";
import { getRecentBVs } from "@/services/benefitsVerification";
import { getActivityTimeline } from "@/services/common";
import { getInvoiceScoreCards, getRecentInvoices, getRecentPayments } from "@/services/invoice";
import { getOrderRequestScoreCards, getRecentOrderRequests } from "@/services/requests";
import { useAuthStore, useClinicStore } from "@/store";
import type { Invoice } from "@/types/invoice";
import type { PaymentInvoiceFeedItem } from "@/types/payment";
import type { FilterType } from "@/types/table";
import type { UserActivityTimeline } from "@/types/users";
import { autoFillIfNotScrollable, formatDate, getRandomColor, makeInfiniteScrollHandler, removeInvoicePrefix, transformBVRecordToPreview } from "@/utils";
import { trackCoreClick } from "@/utils/analytics";
import { timePassed } from "@/utils/bgUtils";
import { calculateChartDateRange } from "@/utils/dateFilters";

type MetricItem = {
  label: string;
  value: string | number;
  color?: string;
  icon?: React.ElementType;
};

type TimelineItem = {
  initials: string;
  name: string;
  time: string;
  note: string;
  color?: string;
};

const DUMMY_TIMELINE: TimelineItem[] = [
  { initials: "JM", name: "Jennifer M.", time: "12h Ago", note: "Called Emory Regarding Pending BV For Sarah Johnson.", color: "teal.500" },
  { initials: "AC", name: "Alex Carter", time: "09h Ago", note: "Reviewed New Supplier Contract", color: "green.500" },
  { initials: "SW", name: "Sophia Williams", time: "08h Ago", note: "Sent Payment Reminder To Green Valley Clinic", color: "blue.500" },
  { initials: "DB", name: "Daniel Brown", time: "08h Ago", note: "Called Riverside Hospital About Pending Approval", color: "yellow.500" },
  { initials: "DB", name: "Daniel Brown", time: "08h Ago", note: "Called Riverside Hospital About Pending Approval", color: "yellow.500" },
  { initials: "DB", name: "Daniel Brown", time: "08h Ago", note: "Called Riverside Hospital About Pending Approval", color: "yellow.500" },
  { initials: "DB", name: "Daniel Brown", time: "08h Ago", note: "Called Riverside Hospital About Pending Approval", color: "yellow.500" },
  { initials: "DB", name: "Daniel Brown", time: "08h Ago", note: "Called Riverside Hospital About Pending Approval", color: "yellow.500" },
];

const BV_STATUSES = getBvStatuses();

const MODAL_INITIAL_STATE = {
  open: false,
  type: "bv-create" as "bv-create" | "bv-view" | "payment" | "order" | "invoice" | "timeline" | "support" | "",
  data: null,
  title: '',
  size: "xl" as 'sm' | 'md' | 'lg' | 'xl' | 'fullwidth',
  step: null,
};

const CLINIC_KEY = 'Account_ID';

// --------------------
// ScoreCard
// --------------------
const ScoreCard = ({
  metrics,
  isLoading,
  tooltipContent,
  onClick,
  dateFilter
}: {
  metrics: MetricItem[];
  isLoading: boolean;
  tooltipContent?: string;
  onClick?: () => void;
  dateFilter?: any;
}) => {
  if (!metrics?.length) return null;

  return (
    <Box
      bg="white"
      borderRadius="lg"
      boxShadow="md"
      border="1px solid"
      borderColor="gray.100"
      minH="142px"
      display="flex"
      flexDirection="column"
      _dark={{ bg: "gray.800", borderColor: "gray.700" }}
      overflow="hidden"
    >
      {/* Header */}
      <Flex
        px={4}
        py={2}
        borderBottom="1px solid"
        borderColor="gray.100"
        _dark={{ borderColor: "gray.700" }}
        align="center"
        justify="space-between"
      >
        <FaceliftText
          fontSize="sm"
          fontWeight="600"
          color="gray.800"
          _dark={{ color: "white" }}
        >
          {metrics[0]?.label}
        </FaceliftText>

        {dateFilter && (
          <Box display="flex" alignItems="center">
            {dateFilter}
          </Box>
        )}
      </Flex>

      {/* Content */}
      <Tooltip
        showArrow
        contentProps={{ css: { display: tooltipContent ? "block" : "none" } }}
        content={tooltipContent || ""}
        positioning={{ placement: "top" }}
        portalled={false}
      >
        <Box
          px={4}
          py={3}
          flex="1"
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor={onClick ? "pointer" : "default"}
          onClick={onClick}
          transition="all 0.2s"
          _hover={onClick ? { bg: "gray.50", _dark: { bg: "gray.600" } } : {}}
        >
          {isLoading ? (
            <HStack gap={6}>
              {Array.from({ length: 3 }).map((_, i) => (
                <VStack key={`skeleton-${i}`} gap={1}>
                  <Skeleton height="14px" width="70px" />
                  <Skeleton height="24px" width="50px" />
                </VStack>
              ))}
            </HStack>
          ) : (
            <HStack justify="center" align="center" gap={6} w="full">
              {metrics.map((metric, index) => (
                <React.Fragment key={`metric-${index}`}>
                  <VStack align="center" flex={1} gap={0}>
                    <FaceliftText
                      fontSize="xs"
                      fontWeight="500"
                      color="gray.500"
                      textAlign={'center'}
                      _dark={{ color: "gray.400" }}
                    >
                      {metric.label}
                    </FaceliftText>

                    <FaceliftText
                      fontSize="xl"
                      fontWeight="700"
                      color={metric.color ?? "gray.900"}
                      _dark={{ color: "white" }}
                    >
                      {metric.value}
                    </FaceliftText>
                  </VStack>

                  {/* Divider except last */}
                  {index < metrics.length - 1 && (
                    <Box
                      h="28px"
                      w="1px"
                      bg="gray.200"
                      _dark={{ bg: "gray.600" }}
                    />
                  )}
                </React.Fragment>
              ))}
            </HStack>
          )}
        </Box>
      </Tooltip>
    </Box>
  );
};

// --------------------
// HomeDashboard
// --------------------
const HomeDashboard = () => {
  const navigate = useNavigate();
  const [brand600] = useToken('colors', ['brand.600']);
  const user = useAuthStore((state) => state.user?.userInformation);

  // State for selected date filter (default to Year to Date)
  const [selectedDateRange, setSelectedDateRange] = useState<
    Record<'invoices' | 'orders' | 'payments' | 'bvs', DateFilterState>
  >({
    invoices: { filter: 'all', startDate: undefined, endDate: undefined },
    orders: { filter: 'all',  startDate: undefined, endDate: undefined },
    payments: { filter: 'all',  startDate: undefined, endDate: undefined },
    bvs: { filter: 'all', startDate: undefined, endDate: undefined },
  });

  const currentClinicsIds = useClinicStore((state) => state.currentClinics).map((c) => c.accountid);
  const [modalInformation, setModalInformation] = useState<{
    open: boolean;
    type: "bv-create" | "bv-view" | "payment" | "order" | "invoice" | "timeline" | "support" | "";
    data: any;
    title: string;
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'fullwidth';
    step?: number | null;
  }>(MODAL_INITIAL_STATE);

  const [isFormDirty, setIsFormDirty] = useState(false);

  const { addInvoiceToCart, selectedInvoices } = useInvoiceCart();

  // Base filters (we DO NOT use filters.page with infinite query)
  const [bvBaseFilters] = useState<FilterType>({
    limit: 50, // ✅ bump to 100 if you want
    globalFilters: JSON.stringify([
      { key: "bv._ta_clinic_value", values: currentClinicsIds },
    ]),
  });

  const [paymentsBaseFilters] = useState<FilterType & { clinics: string[] }>({
    limit: 50,
    clinics: currentClinicsIds,
  });

  const [ordersBaseFilters] = useState<FilterType & { clinics: string[]; port: string }>({
    limit: 50,
    clinics: currentClinicsIds,
    port: user?.portsAvailable || "Woundcare+",
  });

  const [invoicesBaseFilters] = useState<FilterType & { clinics: string[] }>({
    limit: 50,
    clinics: currentClinicsIds,
  });

  const [userActivityTimelineFilters] = useState<FilterType & { userId?: string, limit: number }>({
    limit: 50,
    userId: user?._id
  });
  
  // Scorecards (keep your existing non-infinite hooks) 
  const { data: recentBVCountData, isLoading: bvCountLoading, refetch: refetchBvCount } = useGetRecentBVCount({
    page: 1,
    limit: 50,
    globalFilters: bvBaseFilters.globalFilters,
    dateRangeFilter: selectedDateRange.bvs.startDate && selectedDateRange.bvs.endDate ? `${selectedDateRange.bvs.startDate || selectedDateRange.bvs.endDate && selectedDateRange.bvs.endDate && ''}||${selectedDateRange.bvs.endDate || ''}` : undefined,
    enabled: currentClinicsIds.length > 0,
  });


  const { data: overDueBalance, isLoading: overDueBalanceLoading } = useGetOverDueAmount({
    page: 1,
    limit: 9999,
    clinics: currentClinicsIds,
    dateRangeFilter: selectedDateRange.payments.startDate && selectedDateRange.payments.endDate ? `${selectedDateRange.payments.startDate || selectedDateRange.payments.endDate && selectedDateRange.payments.endDate && ''}||${selectedDateRange.payments.endDate || ''}` : undefined,
    enabled: currentClinicsIds.length > 0,
  });

  const {
    mutate: getBVDetails,
    isLoading: isLoadingBVDetails,
    data: bvDetails,
  } = useGetSingleBV();

  // Get individual order data for details modal
  const { data: orderDetails, isFetching: isOrderDetailsLoading } =
    useGetOrderById(
      modalInformation.type === 'order' && modalInformation.title === 'Order Details'
        ? (modalInformation.data as string)
        : null,
      modalInformation.type === 'order' && modalInformation.title === 'Order Details',
    );

  const accountProducts = useMemo(() => {
    if (bvDetails?.priorAuths && bvDetails?._ta_clinic_value) {
      return (
        bvDetails?.priorAuths?.map((product) => ({
          label: product?.ta_name || '',
          value: String(product.ta_priorauthid) as unknown as number,
          qCode: product.ta_priorauthid,
        })) || []
      );
    }
    return [];
  }, [bvDetails?._ta_clinic_value]);

  const { data: clinicsData, isFetching: isClinicsFetching } = useGetAccounts({
    page: 1,
    limit: 5000,
    enabled: currentClinicsIds.length > 0,
    ids: currentClinicsIds,
  });

  const { mutate: getFile, isLoading: fileLoading } = useGetActivityFile((resp) => {
    const mimeType = resp.headers['content-type'] ?? 'application/octet-stream';
    const blob = new Blob([resp.data], { type: mimeType });
    const fileUrl = URL.createObjectURL(blob);

    window.open(fileUrl, '_blank');

    setTimeout(() => {
      URL.revokeObjectURL(fileUrl);
    }, 100);
  });

  const bvInfinite = useInfiniteQuery(
    ["benefitVerificationsRecentInfinite", bvBaseFilters],
    async ({ pageParam = 1 }) => {
      return getRecentBVs(
        pageParam,
        bvBaseFilters.limit ?? 50,
        bvBaseFilters.globalFilters as string,
      );
    },
    {
      getNextPageParam: (lastPage: any, allPages: any[]) => {
        const limit = bvBaseFilters.limit ?? 50;
        const total = lastPage?.meta?.totalRecords;

        if (typeof total === "number") {
          const loaded = allPages.reduce((sum, p) => sum + (p?.items?.length ?? 0), 0);
          return loaded < total ? allPages.length + 1 : undefined;
        }

        const lastCount = lastPage?.items?.length ?? 0;
        return lastCount === limit ? allPages.length + 1 : undefined;
      },
      enabled: currentClinicsIds.length > 0,
      staleTime: 30_000,
      keepPreviousData: true,
    }
  );

  const paymentsInfinite = useInfiniteQuery(
    ["paymentsRecentInfinite", paymentsBaseFilters],
    async ({ pageParam = 1 }) => {
      // If your payments API supports paging, return { items, meta }.
      // If it returns an array only, wrap it: { items: array, meta: { totalRecords?: ... } }
      return getRecentPayments(
        pageParam,
        paymentsBaseFilters.limit ?? 50,
        paymentsBaseFilters.clinics
      );
    },
    {
      getNextPageParam: (lastPage: any, allPages: any[]) => {
        const limit = paymentsBaseFilters.limit ?? 50;

        const items = Array.isArray(lastPage) ? lastPage : lastPage?.items;
        const meta = Array.isArray(lastPage) ? undefined : lastPage?.meta;

        const total = meta?.totalRecords;
        if (typeof total === "number") {
          const loaded = allPages.reduce((sum, p) => {
            const it = Array.isArray(p) ? p : p?.items;
            return sum + (it?.length ?? 0);
          }, 0);
          return loaded < total ? allPages.length + 1 : undefined;
        }

        const lastCount = items?.length ?? 0;
        return lastCount === limit ? allPages.length + 1 : undefined;
      },
      staleTime: 30_000,
      keepPreviousData: true,
      enabled: currentClinicsIds.length > 0,
    }
  );

  const ordersInfinite = useInfiniteQuery(
    ["ordersRecentInfinite", ordersBaseFilters],
    async ({ pageParam = 1 }) => {
      return getRecentOrderRequests(
        pageParam,
        ordersBaseFilters.limit ?? 50,
        ordersBaseFilters.clinics,
        ordersBaseFilters.port,
      );
    },
    {
      getNextPageParam: (lastPage: any, allPages: any[]) => {
        const limit = ordersBaseFilters.limit ?? 50;
        const total = lastPage?.meta?.totalRecords;

        if (typeof total === "number") {
          const loaded = allPages.reduce((sum, p) => sum + (p?.items?.length ?? 0), 0);
          return loaded < total ? allPages.length + 1 : undefined;
        }

        const lastCount = lastPage?.items?.length ?? 0;
        return lastCount === limit ? allPages.length + 1 : undefined;
      },
      staleTime: 30_000,
      keepPreviousData: true,
      enabled: currentClinicsIds.length > 0,
    }
  );

  const ordersScoreCards = useInfiniteQuery(
    ["ordersRecentInfinite", ordersBaseFilters, selectedDateRange.orders.startDate, selectedDateRange.orders.endDate],
    async ({ pageParam = 1 }) => {
      return getOrderRequestScoreCards(
        pageParam,
        ordersBaseFilters.limit ?? 50,
        ordersBaseFilters.clinics,
        ordersBaseFilters.port,
        selectedDateRange.orders.startDate && selectedDateRange.orders.endDate ? `${selectedDateRange.orders.startDate || ''}||${selectedDateRange.orders.endDate || ''}` : undefined,
      );
    },
    {
      getNextPageParam: (lastPage: any, allPages: any[]) => {
        const limit = ordersBaseFilters.limit ?? 50;
        const total = lastPage?.meta?.totalRecords;

        if (typeof total === "number") {
          const loaded = allPages.reduce((sum, p) => sum + (p?.items?.length ?? 0), 0);
          return loaded < total ? allPages.length + 1 : undefined;
        }

        const lastCount = lastPage?.items?.length ?? 0;
        return lastCount === limit ? allPages.length + 1 : undefined;
      },
      staleTime: 30_000,
      keepPreviousData: true,
      enabled: currentClinicsIds.length > 0,
    }
  );

  const invoicesInfinite = useInfiniteQuery(
    ["invoicesRecentInfinite", invoicesBaseFilters],
    async ({ pageParam = 1 }) => {
      return getRecentInvoices(
        pageParam,
        invoicesBaseFilters.limit ?? 50,
        invoicesBaseFilters.clinics,
      );
    },
    {
      getNextPageParam: (lastPage: any, allPages: any[]) => {
        const limit = invoicesBaseFilters.limit ?? 50;
        const total = lastPage?.meta?.totalRecords;

        if (typeof total === "number") {
          const loaded = allPages.reduce((sum, p) => sum + (p?.items?.length ?? 0), 0);
          return loaded < total ? allPages.length + 1 : undefined;
        }

        const lastCount = lastPage?.items?.length ?? 0;
        return lastCount === limit ? allPages.length + 1 : undefined;
      },
      staleTime: 30_000,
      keepPreviousData: true,
      enabled: currentClinicsIds.length > 0,
    }
  );

  const invoicesScoreCard = useInfiniteQuery(
    [
      "invoicesRecentScoreCard",
      invoicesBaseFilters,
      selectedDateRange.invoices.startDate,
      selectedDateRange.invoices.endDate
    ],
    async ({ pageParam = 1 }) => {
      return getInvoiceScoreCards(
        pageParam,
        invoicesBaseFilters.limit ?? 50,
        invoicesBaseFilters.clinics,
        selectedDateRange.invoices.startDate && selectedDateRange.invoices.endDate ? `${selectedDateRange.invoices.startDate || ''}||${selectedDateRange.invoices.endDate || ''}` : undefined,
      );
    },
    {
      getNextPageParam: (lastPage: any, allPages: any[]) => {
        const limit = invoicesBaseFilters.limit ?? 50;
        const total = lastPage?.meta?.totalRecords;

        if (typeof total === "number") {
          const loaded = allPages.reduce((sum, p) => sum + (p?.items?.length ?? 0), 0);
          return loaded < total ? allPages.length + 1 : undefined;
        }

        const lastCount = lastPage?.items?.length ?? 0;
        return lastCount === limit ? allPages.length + 1 : undefined;
      },
      staleTime: 30_000,
      keepPreviousData: true,
      enabled: currentClinicsIds.length > 0,
    }
  );

  const userActivityTimelineInfinite = useInfiniteQuery(
    ["user-activity-timeline-infinite", invoicesBaseFilters],
    async ({ pageParam = 1 }) => {
      return getActivityTimeline(
        userActivityTimelineFilters.userId || '',
        pageParam,
        userActivityTimelineFilters.limit ?? 50,
      );
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        const limit = userActivityTimelineFilters.limit ?? 50;
        const total = lastPage?.totalRecords;

        if (typeof total === "number") {
          const loaded = allPages.reduce((sum, p) => sum + (p?.annotations?.length ?? 0), 0);
          return loaded < total ? allPages.length + 1 : undefined;
        }

        const lastCount = lastPage?.annotations?.length ?? 0;
        return lastCount === limit ? allPages.length + 1 : undefined;
      },
      staleTime: 30_000,
      keepPreviousData: true,
      refetchInterval: 15_000,
    }
  );

  const { mutate: uploadActivity, isLoading: uploadActivityLoading } = useUploadToActivity((resp) => {
    toast.success('Note added to activity timeline');
    userActivityTimelineInfinite.refetch();
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const filePromises = fileArray.map((file) => {
      return new Promise<{ name: string; base64: string; mimeType: string }>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result as string;
          const base64Data = base64.split(',')[1];

          resolve({
            name: file.name,
            base64: base64Data,
            mimeType: file.type || 'application/octet-stream'
          });
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(filePromises).then((newFiles) => {
      setAttachedFiles((prev) => [...prev, ...newFiles]);
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSendNote = () => {
    if (timelineInputRef.current && timelineInputRef.current.value.trim() !== '') {
      const noteText = timelineInputRef.current.value;

      const files = attachedFiles.length > 0 ? attachedFiles.map(file => ({
        filename: file.name,
        documentbody: file.base64,
        mimetype: file.mimeType
      })) : undefined;

      uploadActivity({
        activityId: user?._id || '',
        text: noteText,
        files
      });

      timelineInputRef.current.value = '';
      setAttachedFiles([]);
    }
  };

  // Flatten pages
  const bvItems = useMemo(() => (bvInfinite.data?.pages ?? []).flatMap((p: any) => p?.items ?? []), [bvInfinite.data]);
  const ordersItems = useMemo(() => (ordersInfinite.data?.pages ?? []).flatMap((p: any) => p?.items ?? []), [ordersInfinite.data]);
  const invoicesItems = useMemo(() => (invoicesInfinite.data?.pages ?? []).flatMap((p: any) => p?.items ?? []), [invoicesInfinite.data]);
  const userActivityTimelineItems = useMemo(() => (userActivityTimelineInfinite.data?.pages ?? []).flatMap((p: any) => p?.item.annotations ?? []), [userActivityTimelineInfinite.data]);

  const paymentsItems = (paymentsInfinite.data?.pages ?? []).flatMap((p: any) =>
    Array.isArray(p) ? p : (p?.items ?? [])
  );

  // Scroll handlers (generic)
  const onBVScroll = useMemo(
    () => makeInfiniteScrollHandler<HTMLDivElement>(bvInfinite, { thresholdPx: 220, throttleMs: 150 }),
    [bvInfinite.hasNextPage, bvInfinite.isFetchingNextPage, bvInfinite.isLoading]
  );

  const onPaymentsScroll = useMemo(
    () => makeInfiniteScrollHandler<HTMLDivElement>(paymentsInfinite, { thresholdPx: 220, throttleMs: 150 }),
    [paymentsInfinite.hasNextPage, paymentsInfinite.isFetchingNextPage, paymentsInfinite.isLoading]
  );

  const onOrdersScroll = useMemo(
    () => makeInfiniteScrollHandler<HTMLDivElement>(ordersInfinite, { thresholdPx: 220, throttleMs: 150 }),
    [ordersInfinite.hasNextPage, ordersInfinite.isFetchingNextPage, ordersInfinite.isLoading]
  );

  const onInvoicesScroll = useMemo(
    () => makeInfiniteScrollHandler<HTMLDivElement>(invoicesInfinite, { thresholdPx: 220, throttleMs: 150 }),
    [invoicesInfinite.hasNextPage, invoicesInfinite.isFetchingNextPage, invoicesInfinite.isLoading]
  );

  const onUserActivityTimelineScroll = useMemo(
    () => makeInfiniteScrollHandler<HTMLDivElement>(userActivityTimelineInfinite, { thresholdPx: 220, throttleMs: 150 }),
    [userActivityTimelineInfinite.hasNextPage, userActivityTimelineInfinite.isFetchingNextPage, userActivityTimelineInfinite.isLoading]
  );

  // Optional: auto-fill if not scrollable (better UX)
  const bvScrollRef = useRef<HTMLDivElement | null>(null);
  const paymentsScrollRef = useRef<HTMLDivElement | null>(null);
  const ordersScrollRef = useRef<HTMLDivElement | null>(null);
  const invoicesScrollRef = useRef<HTMLDivElement | null>(null);
  const userActivityTimelineScrollRef = useRef<HTMLDivElement | null>(null);
  const timelineInputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [attachedFiles, setAttachedFiles] = useState<Array<{ name: string; base64: string; mimeType: string }>>([]);
  useEffect(() => {
    if (currentClinicsIds.length === 0) return;
    autoFillIfNotScrollable(bvScrollRef.current, bvInfinite);
  }, [currentClinicsIds, bvInfinite.data, bvInfinite.hasNextPage]);

  useEffect(() => {
    if (currentClinicsIds.length === 0) return;
    autoFillIfNotScrollable(paymentsScrollRef.current, paymentsInfinite);
  }, [paymentsInfinite.data, paymentsInfinite.hasNextPage]);

  useEffect(() => {
    if (currentClinicsIds.length === 0) return;
    autoFillIfNotScrollable(ordersScrollRef.current, ordersInfinite);
  }, [ordersInfinite.data, ordersInfinite.hasNextPage]);

  useEffect(() => {
    if (currentClinicsIds.length === 0) return;
    autoFillIfNotScrollable(invoicesScrollRef.current, invoicesInfinite);
  }, [invoicesInfinite.data, invoicesInfinite.hasNextPage]);

  useEffect(() => {
    autoFillIfNotScrollable(userActivityTimelineScrollRef.current, userActivityTimelineInfinite);
  }, [userActivityTimelineInfinite.data, userActivityTimelineInfinite.hasNextPage]);

  // Additional state for 'open' variant (multiple invoice viewing)
  const [viewMultipleInvoicesModal, setViewMultipleInvoicesModal] =
    useState<boolean>(false);
  const [activeInvoiceInList, setActiveInvoiceInList] =
    useState<Invoice | null>(null);

  // Determine which invoice to fetch based on active modal (for multi-modal support)
  const invoiceNumberToFetch =
    viewMultipleInvoicesModal && activeInvoiceInList
      ? activeInvoiceInList.Invoice
      : undefined;

  // Build a map of clinic GUID to QBO ID for dynamic lookup
  const clinicsQBOIds: Record<string, string> = useMemo(() => {
    if (!clinicsData?.items) return {};
    return clinicsData.items.reduce(
      (acc, clinic) => {
        acc[`{${clinic.accountid.toLowerCase()}}`] = clinic.ta_qbocustomerid;
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [clinicsData]);

  const clinicQBOIdToFetch =
    viewMultipleInvoicesModal && activeInvoiceInList
      ? clinicsQBOIds[activeInvoiceInList[CLINIC_KEY]?.toLowerCase()] || ''
      : undefined;
  // Use PDF viewer hook with multi-modal support
  const {
    viewInvoiceModal,
    pdfViewerUrl,
    isFetchingPDF,
    isEmptyOrUnavailable,
    isPdfLoadError,
    setViewInvoiceModal,
    setSelectedInvoiceNumber,
    setSelectedClinicQBOId,
    resetPDFViewer,
  } = useInvoicePDFViewer({
    invoiceNumberToFetch,
    clinicQBOIdToFetch,
  });

  const handleViewInvoice = useCallback(
    (invoiceNumber: string, clinicQBOId: string) => {
      if (!clinicQBOId) {
        toast.error('Clinic QBO ID not found for the selected invoice');
        return;
      }

      // Set invoice details to trigger the PDF fetch via useInvoicePDFViewer hook
      setSelectedInvoiceNumber(invoiceNumber);
      setSelectedClinicQBOId(clinicQBOId);
      setViewInvoiceModal(true);
    },
    [setSelectedInvoiceNumber, setSelectedClinicQBOId, setViewInvoiceModal],
  );

  const handleLinkClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    const link = target.closest("a");
    if (!link) return;

    const href = link.getAttribute("href");
    if (!href) return;

    const isRichTextAttachment = link.classList.contains("rte_attachment_pdf") || href.includes('msdyn_richtextfiles');

    if (isRichTextAttachment) {
      e.preventDefault();

      const splittedHref = href.split('msdyn_richtextfiles').at(1);
      const fileUrl = splittedHref ? `/msdyn_richtextfiles${splittedHref}` : href;

      getFile({ url: fileUrl });
    }
  };


  const handleDateFilterChange = (
    value: string | null,
    key: 'invoices' | 'orders' | 'payments' | 'bvs',
  ) => {
    const date = calculateChartDateRange(value as string);
    setSelectedDateRange((prev) => ({
      ...prev,
      [key]: {
        filter: value,
        startDate: date?.start || undefined,
        endDate: date?.end || undefined,
      },
    }));
  };

  return (
    <Box maxH="90vh" px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} gap="4" alignItems="stretch">
        {/* ✅ Benefits Verification (Infinite) */}
        <Panel
          title="📋  Benefits Verification"
          addButton={{
            permission: 'BenefitVerification:Create', title: "Benefits Verification", onClick: () => {
              trackCoreClick('create_bv_clicked', {
                source_module: 'dashboard',
              });
              setModalInformation({
                open: true,
                type: "bv-create",
                data: null,
                title: "New Benefits Verification",
                size: "xl",
                step: 1,
              });
            }
          }}
          scoreCard={
            <ScoreCard
              key={"BV's Submitted"}
              tooltipContent="Click to view all benefits verifications"
              onClick={() => navigate('/benefits-verifications')}
              metrics={[
                { icon: ({ width, height, color, fill }: { width?: number; height?: number; color?: string; fill?: string }) => <HiOutlineClipboardDocumentCheck size={width ?? height ?? 36} color={color ?? fill ?? "white"} strokeWidth={1.5} />, label: "Pending BVs", value: recentBVCountData?.pendingCount ?? 0 },
                { label: "Approved BVs", value: recentBVCountData?.approvedCount || 0, color: "green.500" },
                { label: "Denied BVs", value: recentBVCountData?.deniedCount || 0, color: "red.400" },
              ]}
              isLoading={bvCountLoading}
              dateFilter={
                <ScoreCardDateFilter
                  onDateFilterChange={(value) => handleDateFilterChange(value, 'bvs')}
                  defaultValue="all"
                />
              }
            />
          }
        >
          <Box ref={bvScrollRef} maxH="70vh" scrollbar={'hidden'} overflowY="auto" pr="1" onScroll={onBVScroll}>
            <ListPanel
              items={(bvItems || []).map((bv: any) => {
                const statusCode = bvStatusMap[bv.ta_bv_status as number];
                return {
                  id: bv?.ta_benefitsverificationid || "",
                  name: bv.patient?.fullname || "Unknown Patient",
                  title: user?.role?.toLowerCase() === 'admin' ? bv?.ta_bvidfull : "",
                  clinic: bv.account?.name,
                  subtitleLeft: `👨‍⚕️ ${bv.provider?.fullname || "Unknown Provider"}`,
                  subtitleRight: `📦 ${bv.priorAuths.length > 1 ? "Multiple Products" : bv?.priorAuths.at(0)?.ta_name}`,
                  status: {
                    label: getBVStatus(statusCode || "In Process").label,
                    colorScheme: getBVStatus(statusCode || "In Process").color,
                  },
                  popoverData: [
                    user?.role?.toLowerCase() === 'admin' && { label: "BV ID", value: bv?.ta_bvidfull || "" },
                    { label: "Provider", value: `👨‍⚕️ ${bv.provider?.fullname || "-"}` },
                    { label: "Patient Name", value: bv.patient?.fullname || "-" },
                    { label: "Clinic", value: bv.account?.name || "-" },
                    { label: "Insurance", value: bv.patient?.primaryInsurance?.ta_name || "-" },
                    { label: "Product", value: bv.priorAuths.length > 1 ? "📦 Multiple Products" : bv?.priorAuths.at(0)?.ta_name || "-" },
                    { label: "Submission Date", value: formatDate(bv.ta_proceduredate) },
                    { label: "", value: "" },
                    { label: "", value: "" },
                    {
                      label: "", value: <FaceliftButton borderRadius={"md"} px={"10"} onClick={() => {
                        setModalInformation({
                          open: true,
                          type: "bv-view",
                          data: bv,
                          title: `BV Details - ${bv?.ta_bvidfull || ""}`
                        });
                        getBVDetails(bv?.ta_benefitsverificationid);
                      }}>Review</FaceliftButton>
                    },
                  ].filter(Boolean) as { label: string; value: string | React.ReactNode }[],
                };
              })}
              isLoading={bvInfinite.isLoading}
            />

            {bvInfinite.isFetchingNextPage ? (
              <Center py="3">
                <Spinner size="sm" />
              </Center>
            ) : null}
          </Box>
        </Panel>

        {/* ✅ Payments (Infinite) */}
        <Panel
          title="💳  Payments"
          addButton={{
            permission: 'SupportTickets:Create', title: "Payment", tooltip: 'View all payments',
            icon: <FaEye size={18} strokeWidth={2.5} stroke="currentColor" />,
            onClick: () => {
              navigate('/paid-invoices')
            }
          }}
          scoreCard={
            <ScoreCard
              key="Overdue Balance"
              tooltipContent="Click to open invoices"
              onClick={() => navigate('/all-invoices')}
              metrics={[
                { icon: ({ width, height, color, fill }: { width?: number; height?: number; color?: string; fill?: string }) => <HiOutlineDocumentCurrencyDollar size={width ?? height ?? 36} color={color ?? fill ?? "white"} strokeWidth={1.5} />, label: "Overdue Balance", value: `$${overDueBalance?.amountDue?.toLocaleString?.() ?? 0}`, color: "red.400" },
                { label: "Paid Balance", value: `$${overDueBalance?.amountPaid?.toLocaleString?.() ?? 0}`, color: "green.500" },
              ]}
              isLoading={overDueBalanceLoading}
              dateFilter={
                <ScoreCardDateFilter
                  onDateFilterChange={(value) => handleDateFilterChange(value, 'payments')}
                  defaultValue="all"
                />
              }
            />
          }
        >
          <Box ref={paymentsScrollRef} maxH="70vh" overflow="auto" scrollbar='hidden' pr="1" onScroll={onPaymentsScroll}>
            <ListPanel
              items={(paymentsItems || []).map((payment: PaymentInvoiceFeedItem) => ({
                id: payment.id,
                title: payment.paymentReport.InvoiceNumber,
                name: `$${payment.paymentReport.PaidAmount}`,
                clinic: payment.paymentReport.ClinicName,
                subtitleLeft: `👨‍⚕️ ${payment.paymentReport.Username}`,
                subtitleRight: `📦 ${payment.paymentReport.Product}`,
                status: { label: "Paid", colorScheme: colors.success },
                popoverData: [
                  user?.role?.toLowerCase() === 'admin' && { label: "Payment ID", value: payment.paymentReport.PaymentIndentId },
                  { label: "Invoice Number", value: payment.paymentReport.InvoiceNumber },
                  { label: "Payment Status", value: "Paid" },
                  { label: "Invoice Date", value: formatDate(payment.paymentReport.InvoiceDate) },
                  { label: "Account/Clinic", value: payment.paymentReport.ClinicName },
                  { label: "Product", value: `📦 ${payment.paymentReport.Product}` },
                  { label: "Payment Amount", value: `$${payment.paymentReport?.PaidAmount?.toFixed(2) ?? 0}` },
                  { label: "Payment Date", value: formatDate(payment.paymentReport.SubmissionDate) },
                  {
                    label: "", value: <FaceliftButton disabled={isClinicsFetching} loading={isClinicsFetching} borderRadius={"md"} px={"10"} onClick={() => {
                      const clinicQBOId = clinicsQBOIds[payment.paymentReport.ClinicGUID?.toLowerCase()] || '';
                      handleViewInvoice(payment.paymentReport.InvoiceNumber, clinicQBOId);
                    }}>Review</FaceliftButton>
                  },
                ].filter(Boolean) as { label: string; value: string | React.ReactNode }[],
              }))}
              isLoading={paymentsInfinite.isLoading}
            />

            {paymentsInfinite.isFetchingNextPage ? (
              <Center py="3">
                <Spinner size="sm" />
              </Center>
            ) : null}
          </Box>
        </Panel>

        {/* ✅ Shipped Orders (Infinite) */}
        <Panel
          title="📦  Shipped Orders"
          addButton={{
            permission: user?.portsAvailable === 'SalesRep' ? 'OrderConsignmentManagment:Create' : 'Orders:Create', title: "Order", onClick: () => {
              trackCoreClick('create_order_clicked', {
                source_module: 'dashboard',
                order_kind: user?.portsAvailable === 'SalesRep' ? 'consignment' : 'patient',
              });
              setModalInformation({
                open: true,
                type: "order",
                data: null,
                title: user?.portsAvailable === 'SalesRep' ? "New Consignment Order" : ['Woundcare+', 'Mindsight'].includes(user?.portsAvailable as string) ? `New Patient Order` : "New Order Request",
                size: "xl",
              });
            }
          }}
          scoreCard={
            <ScoreCard
              key={"Orders Placed"}
              tooltipContent="Click to open orders"
              onClick={() => navigate(user?.portsAvailable === 'SalesRep' ? '/consignments' : '/requests')}
              metrics={[
                { icon: () => <OrdersIcon width={50} height={50} />, label: "Orders Placed", value: ordersScoreCards.data?.pages?.[0].newOrders ?? 0 },
                { label: "Shipped Orders", value: ordersScoreCards.data?.pages?.[0]?.meta?.totalRecords ?? 0, color: "green.500" },
              ]}
              isLoading={ordersScoreCards.isFetching}
              dateFilter={
                <ScoreCardDateFilter
                  onDateFilterChange={(value) => handleDateFilterChange(value, 'orders')}
                  defaultValue="all"
                />
              }
            />
          }
        >
          <Box ref={ordersScrollRef} maxH="70vh" scrollbar={'hidden'} overflowY="auto" pr="1" onScroll={onOrdersScroll}>
            <ListPanel
              items={(ordersItems || []).map((order: any) => {
                const latestShipment = [...order.shipments]
                  .filter((s) => s?.createdon)
                  .sort(
                    (a, b) =>
                      new Date(b.createdon || '').getTime() - new Date(a.createdon || '').getTime()
                  )?.[0];
                const isAdmin = user?.role === 'Admin';
                return {
                  id: order.ta_orderrequestid,
                  title: isAdmin ? order?.ta_name : "",
                  name: order.ta_BVPatient?.fullname || "",
                  clinic: order.ta_Clinic?.name,
                  subtitleRight: `📦 ${order.ta_SKU?.ta_name || "Unknown Product"}`,
                  status: {
                    label: getRequestStatus(order?.ta_status).label,
                    colorScheme: getRequestStatus(order?.ta_status).color,
                  },
                  popoverData: [
                    user?.role?.toLowerCase() === 'admin' && { label: "Order ID", value: order?.ta_name || "-" },
                    { label: "Tissue ID", value: order.shipments?.map((s: any) => s.ta_tissueid).join(", ") || "—" },
                    { label: "Product Name", value: `📦 ${order.ta_SKU?.ta_name}` },
                    { label: "Account/Clinic", value: order.ta_Clinic?.name },
                    { label: "Patient", value: order.ta_BVPatient?.fullname || "-" },
                    { label: "Tracking #", value: `${latestShipment?.ta_trackingnumber || "-"}`, link: `https://www.fedex.com/wtrk/track/?trknbr=${latestShipment?.ta_trackingnumber}` },
                    { label: "Carrier", value: `${order.ta_shippingcarrier ? (order.ta_shippingcarrier === 953870000 ? "FedEx" : "UPS") : "-"}` },
                    { label: "Order Status", value: getRequestStatus(order.ta_status).label },
                    {
                      label: "", value: <FaceliftButton borderRadius={"md"} px={"10"} onClick={() => {
                        setModalInformation({
                          open: true,
                          type: "order",
                          data: order.ta_orderrequestid,
                          title: `Order Details`
                        });
                      }}>Review</FaceliftButton>
                    }
                  ].filter(Boolean) as { label: string; value: string | React.ReactNode }[],
                };
              })}
              isLoading={ordersInfinite.isLoading}
            />

            {ordersInfinite.isFetchingNextPage ? (
              <Center py="3">
                <Spinner size="sm" />
              </Center>
            ) : null}
          </Box>
        </Panel>

        {/* ✅ Invoices (Infinite) */}
        <Panel
          title="📄  Invoices"
          addButton={{
            permission: 'Invoice:Pay', title: "Pay Invoice", tooltip: "Pay Invoice",
            icon: <FaCartShopping />,
            onClick: () => {
              trackCoreClick('pay_invoice_clicked', {
                source_module: 'dashboard',
              });
              navigate('/invoice-cart');
            }
          }}
          scoreCard={
            <ScoreCard
              key={"Invoices Created"}
              tooltipContent="Click to view all invoices"
              onClick={() => navigate('/all-invoices')}
              metrics={[
                { icon: () => <InvoiceIcon width={30} height={30} />, label: "Invoices Created", value: invoicesScoreCard.data?.pages?.[0]?.meta?.totalRecords ?? 0 },
                { label: "Paid Invoices", value: invoicesScoreCard.data?.pages?.[0]?.paidInvoicesCount ?? 0, color: "green.500" },
                { label: "Overdue Invoices", value: invoicesScoreCard.data?.pages?.[0]?.overDueInvoicesCount ?? 0, color: "red.400" },
              ]}
              isLoading={invoicesScoreCard.isFetching}
              dateFilter={
                <ScoreCardDateFilter
                  onDateFilterChange={(value) => handleDateFilterChange(value, 'invoices')}
                  defaultValue="all"
                />
              }
            />
          }
        >
          <Box ref={invoicesScrollRef} maxH="70vh" scrollbar={'hidden'} overflowY="auto" pr="1" onScroll={onInvoicesScroll}>
            <ListPanel
              items={(invoicesItems || []).map((invoice: any) => {
                invoicesScoreCard
                const clinicQBOId = clinicsQBOIds[invoice['invoice_Account_ID']?.toLowerCase()] || '';
                const invoicePayload = { ...removeInvoicePrefix(invoice), clinicQBOId };
                const isAlreadyInCart = selectedInvoices.some(
                  (selectedInvoice) => selectedInvoice.Invoice === invoicePayload.Invoice
                );
                return ({
                  id: invoice.invoice_InvoiceID,
                  title: invoice.invoice_Invoice,
                  name: `$${invoice.invoice_InvoiceCost?.toFixed(2) ?? 0}`,
                  clinic: invoice.invoice_Clinic,
                  subtitleLeft: `👨‍⚕️ ${invoice.invoice_PhysicianName}`,
                  subtitleRight: `📦 ${invoice.invoice_Product}`,
                  status: {
                    label: statusInvoicesFeed(invoice),
                    colorScheme: getInvoiceStatusFeed(statusInvoicesFeed(invoice)?.toLowerCase()).color,
                  },
                  popoverData: [
                    { label: "Invoice Number", value: invoice.invoice_Invoice },
                    { label: "Invoice Status", value: invoice.invoice_InvoiceStatus },
                    { label: "Invoice Date", value: formatDate(invoice.invoice_InvoiceDate) },
                    { label: "Aging", value: timePassed(invoice.invoice_InvoiceDate) },
                    { label: "Product", value: `📦 ${invoice.invoice_Product}` },
                    { label: "Account/Clinic", value: invoice.invoice_Clinic },
                    { label: "Provider/Physician", value: `👨‍⚕️ ${invoice.invoice_PhysicianName || "-"}` },
                    { label: "Sales Rep", value: invoice.invoice_MasterRep || "-" },
                    { label: "Invoice Cost", value: `$${invoice.invoice_InvoiceCost?.toFixed(2) ?? 0}` },
                    { label: "Balance", value: `$${invoice.invoice_QBAmtDue?.toFixed(2) ?? 0}` },
                    {
                      label: "", value: <FaceliftButton disabled={isClinicsFetching} loading={isClinicsFetching} borderRadius={"md"} px={"10"} onClick={() => {
                        const clinicQBOId = clinicsQBOIds[invoice['invoice_Account_ID']?.toLowerCase()] || '';
                        handleViewInvoice(invoice.invoice_Invoice, clinicQBOId);
                      }}>Review</FaceliftButton>
                    },
                    {
                      label: "", value:
                        <FaceliftButton disabled={isClinicsFetching || isAlreadyInCart} loading={isClinicsFetching} borderRadius={"md"} px={"10"} onClick={() => {
                          trackCoreClick('invoice_add_to_cart_clicked', {
                            source_module: 'dashboard',
                            invoice_id: invoicePayload.InvoiceID,
                            invoice_number: invoicePayload.Invoice,
                            clinic_id: invoicePayload.Account_ID,
                            already_in_cart: isAlreadyInCart,
                          });
                          if (!isAlreadyInCart) {
                            addInvoiceToCart(invoicePayload);
                          }
                        }}>{isAlreadyInCart ? "Already in Cart" : "Add to Cart"}</FaceliftButton>
                    }
                  ],
                })
              })}
              isLoading={invoicesInfinite.isLoading}
            />

            {invoicesInfinite.isFetchingNextPage ? (
              <Center py="3">
                <Spinner size="sm" />
              </Center>
            ) : null}
          </Box>
        </Panel>
        {/* Timeline (unchanged) */}
        <Panel
          title="💬 Activity Timeline"
          addButton={{ title: "Patient", onClick: () => { } }}
          scoreCard={
            <ScoreCard
              key={"Activity Timeline"}
              metrics={[{ icon: () => <CalendarIcon width={30} height={30} />, label: "Activity Timeline", value: (userActivityTimelineInfinite?.data?.pages?.at(0) as any)?.item.totalRecords ?? 0 }]}
              isLoading={userActivityTimelineInfinite.isLoading}
            />
          }
        >
          <Flex direction="column" maxH={{ base: "70vh", lg: "70vh" }}>
            <Box flex="1" overflowY="auto" scrollbar={'hidden'} pr="1" ref={userActivityTimelineScrollRef} onScroll={onUserActivityTimelineScroll}>
              <VStack align="stretch" gap="0">
                <Show when={userActivityTimelineInfinite.isLoading}>
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <React.Fragment key={`tl-sk-${idx + 1}`}>
                      <HStack px="3" py="3" gap="3" align="start" borderRadius="md">
                        <SkeletonCircle size="10" />
                        <Box flex="1">
                          <HStack align="start">
                            <Skeleton height="12px" width="120px" />
                            <Spacer />
                            <Skeleton height="10px" width="44px" />
                          </HStack>
                          <SkeletonText mt="2" noOfLines={2} gap="2" />
                        </Box>
                      </HStack>
                      {idx !== 4 ? <Separator borderColor="gray.100" /> : null}
                    </React.Fragment>
                  ))}
                </Show>

                <Show when={!userActivityTimelineInfinite.isLoading && userActivityTimelineItems.length > 0}>
                  {userActivityTimelineItems.map((item: UserActivityTimeline, idx) => {
                    const username = item.createdby.fullname.toLowerCase().includes('dynamic') ? `${user?.firstName} ${user?.lastName}` : item.createdby.fullname
                    const avatarColor = getRandomColor(username);
                    const hasAttachment = item.isdocument && !!item.documentbody;
                    const hasRichTextAttachment = !item.isdocument && item.notetext?.includes('msdyn_richtextfiles');
                    const showAttachmentButtons = hasAttachment || hasRichTextAttachment;

                    const handleViewDocument = () => {
                      if (hasAttachment) {
                        const byteCharacters = atob(item.documentbody);
                        const byteNumbers = new Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) {
                          byteNumbers[i] = byteCharacters.charCodeAt(i);
                        }
                        const byteArray = new Uint8Array(byteNumbers);
                        const blob = new Blob([byteArray], { type: item.mimetype });
                        const url = URL.createObjectURL(blob);
                        window.open(url, '_blank');
                      } else if (hasRichTextAttachment) {
                        const parser = new DOMParser();
                        const doc = parser.parseFromString(item.notetext, 'text/html');
                        const link = doc.querySelector('a[href*="msdyn_richtextfiles"]');
                        if (link) {
                          const href = link.getAttribute('href');
                          if (href) {
                            const splittedHref = href.split('msdyn_richtextfiles').at(1);
                            const fileUrl = splittedHref ? `/msdyn_richtextfiles${splittedHref}` : href;
                            getFile({ url: fileUrl });
                          }
                        }
                      }
                    };

                    const handleDownloadDocument = () => {
                      if (hasAttachment) {
                        const byteCharacters = atob(item.documentbody);
                        const byteNumbers = new Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) {
                          byteNumbers[i] = byteCharacters.charCodeAt(i);
                        }
                        const byteArray = new Uint8Array(byteNumbers);
                        const blob = new Blob([byteArray], { type: item.mimetype });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = item.filename;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      } else if (hasRichTextAttachment) {
                        const parser = new DOMParser();
                        const doc = parser.parseFromString(item.notetext, 'text/html');
                        const link = doc.querySelector('a[href*="msdyn_richtextfiles"]');
                        if (link) {
                          const href = link.getAttribute('href');
                          const filename = link.textContent || 'document';
                          if (href) {
                            const splittedHref = href.split('msdyn_richtextfiles').at(1);
                            const fileUrl = splittedHref ? `/msdyn_richtextfiles${splittedHref}` : href;

                            fetch(`${import.meta.env.VITE_COMMON_URL}/common/get-activity-file?url=${encodeURIComponent(fileUrl)}`)
                              .then(response => response.blob())
                              .then(blob => {
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = filename;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(url);
                              })
                              .catch(() => toast.error('Failed to download file'));
                          }
                        }
                      }
                    };

                    return (
                      <React.Fragment key={`${idx}`}>
                        <HStack
                          px="2"
                          py="2"
                          gap="2"
                          align="start"
                          borderRadius="md"
                          transition="background 150ms ease"
                          _hover={{
                            _dark: { bg: "gray.500" },
                            _light: { bg: "gray.100" },
                          }}
                        >
                          <FaceliftAvatarNoBadge size="xs" name={username || ''} colorPalette={avatarColor} />
                          <Box flex="1" minW={0}>
                            <HStack align="start" mb="1">
                              <FaceliftText
                                fontWeight="800"
                                color={{ _dark: "gray.300", _light: "gray.900" }}
                                fontSize="sm"
                                lineHeight="1.1"
                              >
                                {username}
                              </FaceliftText>
                              <Spacer />
                              <FaceliftText fontSize="xs" color="gray.500" fontWeight="600">
                                {item.createdon ? timePassed(item.createdon) : ""}
                              </FaceliftText>
                            </HStack>
                            <Show when={!!item.subject}>
                              <FaceliftText fontSize={'sm'}>
                                {item.subject}
                              </FaceliftText>
                            </Show>
                            <Flex direction="row" align="center" justify="space-between" gap="2">
                              <Box
                                className="note-html"
                                fontSize="sm"
                                onClick={handleLinkClick}
                                dangerouslySetInnerHTML={{ __html: item.notetext }}
                                flex="1"
                                minW={0}
                              />
                              <Show when={hasRichTextAttachment && !hasAttachment}>
                                <Flex gap="2" align="center" flexShrink={0}>
                                  <Tooltip content="View" positioning={{ placement: 'top' }}>
                                    <FaceliftIconButton
                                      onClick={handleViewDocument}
                                      title="View"
                                      size={'2xs'}
                                    >
                                      <HiOutlineEye size={12} />
                                    </FaceliftIconButton>
                                  </Tooltip>
                                  <Tooltip content="Download" positioning={{ placement: 'top' }}>
                                    <IconButton
                                      size="2xs"
                                      variant="ghost"
                                      colorScheme="green"
                                      aria-label="Download attachment"
                                      onClick={handleDownloadDocument}
                                      border={'1px solid'}
                                      rounded={'50%'}
                                      _hover={{ bg: 'brand.solidGradient', color: 'white' }}
                                    >
                                      <HiOutlineDownload size={16} />
                                    </IconButton>
                                  </Tooltip>
                                </Flex>
                              </Show>
                            </Flex>
                            <Show when={hasAttachment}>
                              <HStack mt="2" gap="2" align="center">
                                <FaceliftText fontSize={'xs'} color={colors.success} flex="1" truncate>
                                  📎 {item.filename}
                                </FaceliftText>
                                <Tooltip content="View" positioning={{ placement: 'top' }}>
                                  <FaceliftIconButton
                                    onClick={handleViewDocument}
                                    title="View"
                                    size={'2xs'}
                                  >
                                    <HiOutlineEye size={12} />
                                  </FaceliftIconButton>
                                </Tooltip>
                                <Tooltip content="Download" positioning={{ placement: 'top' }}>
                                  <IconButton
                                    size="2xs"
                                    variant="ghost"
                                    colorScheme="green"
                                    aria-label="Download attachment"
                                    onClick={handleDownloadDocument}
                                    border={'1px solid'}
                                    rounded={'50%'}
                                    _hover={{ bg: 'brand.solidGradient', color: 'white' }}
                                  >
                                    <HiOutlineDownload size={16} />
                                  </IconButton>
                                </Tooltip>
                              </HStack>
                            </Show>

                          </Box>
                        </HStack>

                        <Show when={idx !== userActivityTimelineItems.length - 1}>
                          <Separator borderColor="gray.100" />
                        </Show>
                      </React.Fragment>
                    );
                  })}
                </Show>
              </VStack>
            </Box>
            <Box pt="3" px="2" pb="2" borderTopWidth="1px" borderTopColor="gray.400">
              <Show when={attachedFiles.length > 0}>
                <VStack align="stretch" gap="1" mb="2">
                  {attachedFiles.map((file, index) => (
                    <HStack key={`file-${index + 1}`} px="2" py="1" bg={{ _dark: 'gray.500', _light: 'gray.100' }} borderRadius="md" justify="space-between">
                      <HStack gap="2" flex="1" minW={0}>
                        <HiPaperClip size={14} />
                        <FaceliftText fontSize="xs" truncate flex="1">
                          {file.name}
                        </FaceliftText>
                      </HStack>
                      <IconButton
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        aria-label="Remove file"
                        onClick={() => handleRemoveFile(index)}
                        minW="auto"
                        h="auto"
                      >
                        <HiX size={16} />
                      </IconButton>
                    </HStack>
                  ))}
                </VStack>
              </Show>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                style={{ display: "none" }}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.webp,.xls,.xlsx,.csv,.txt"
                multiple
              />

              <Grid
                w="full"
                gap="2"
                templateColumns={{
                  base: "40px 1fr",
                  sm: "40px 1fr auto",
                }}
                templateRows={{
                  base: "auto auto",
                  sm: "auto",
                }}
                alignItems="center"
              >
                <IconButton
                  size="sm"
                  variant="ghost"
                  aria-label="Attach file"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadActivityLoading}
                  gridColumn="1"
                >
                  <HiPaperClip size={20} />
                </IconButton>

                <Input
                  ref={timelineInputRef}
                  placeholder="Add Note.."
                  borderRadius="md"
                  _focusVisible={{
                    borderColor: "teal.600",
                    boxShadow: "0 0 0 1px var(--chakra-colors-teal-600)",
                  }}
                  gridColumn="2"
                  minW="0"
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      (e.target as HTMLInputElement).value.trim() !== ""
                    ) {
                      handleSendNote();
                    }
                  }}
                />

                <FaceliftButton
                  colorScheme="teal"
                  borderRadius="md"
                  transition="all 150ms ease"
                  _hover={{ transform: "translateY(-1px)" }}
                  _active={{ transform: "translateY(0px)" }}
                  disabled={uploadActivityLoading}
                  onClick={handleSendNote}
                  gridColumn={{ base: "1 / span 2", sm: "3" }}
                  w={{ base: "full", sm: "auto" }}
                  px="8"
                  loading={uploadActivityLoading}
                >
                  Send
                </FaceliftButton>
              </Grid>
            </Box>
          </Flex>
        </Panel>
      </SimpleGrid>

      <FaceliftDialogWithExitConfirmation
        open={modalInformation.open}
        onOpenChange={(openState) => {
          if (!openState) {
            setModalInformation({ open: false, type: "", data: null, title: '' });
          }
        }}
        title={modalInformation.title}
        showBackBtn
        sx={{
          boxShadow: `0 0 20px 0px ${brand600}`,
          ...(modalInformation?.size === 'fullwidth' && {
            width: '95vw',
            maxWidth: '95vw',
          }),
        }}
        isDirty={isFormDirty}
        size={modalInformation?.size || 'xl'}
        onInteractOutside={(e: { detail?: { originalEvent?: { target?: EventTarget | null } }; preventDefault: () => void }) => {
          const target = e?.detail?.originalEvent
            ?.target as HTMLElement | undefined;
          if (target?.closest('[data-facelift-date-picker-portal]')) {
            e.preventDefault();
          }
        }}
      >
        <Show when={isLoadingBVDetails || isOrderDetailsLoading}>
          <FaceliftDataLoader
            title="Loading details..."
            description={`Please wait while we fetch the details.`}
          />
        </Show>
        <Show when={!isLoadingBVDetails && modalInformation.type === "bv-view" && !!bvDetails}>
          <Preview
            bvInformation={transformBVRecordToPreview(
              bvDetails || null,
              accountProducts,
            )}
          />
        </Show>

        <Show when={modalInformation.type === "bv-create" && modalInformation.title === 'New Benefits Verification' && !isLoadingBVDetails}>
          <BenefitVerificationForm
            customFunction={(data) => {
              setModalInformation((prev) => ({
                ...prev,
                type: "bv-create",
                step: data?.step
              }));
            }}
            onSuccess={() => {
              setModalInformation(MODAL_INITIAL_STATE);
              bvInfinite.refetch();
              refetchBvCount();
            }}
            id={modalInformation?.data}
            onIsDirtyChange={setIsFormDirty}
          />
        </Show>

        <Show when={modalInformation?.type === 'support'}>
          <SupportTicketForm
            mode={'create'}
            onSuccess={() => {
              setModalInformation(MODAL_INITIAL_STATE);
            }}
            onIsDirtyChange={setIsFormDirty}
          />
        </Show>
        <Show when={modalInformation?.type === 'order' && (modalInformation.title !== 'Order Details')}>
          <Show when={['Woundcare+', 'Mindsight'].includes(user?.portsAvailable || '')}>
            <OrderRequestForm
              mode={'create'}
              onSuccess={() => {
                setModalInformation(MODAL_INITIAL_STATE);
              }}
              onIsDirtyChange={setIsFormDirty}
            />
          </Show>
          <Show when={user?.portsAvailable === 'SalesRep'}>
            <BulkConsignmentOrderForm
              mode={'create'}
              onSuccess={() => {
                setModalInformation(MODAL_INITIAL_STATE);
              }}
              onIsDirtyChange={setIsFormDirty}
            />
          </Show>
        </Show>

        <Show when={modalInformation?.type === 'order' && modalInformation.title === 'Order Details' && !isOrderDetailsLoading}>
          <DetailsView
            data={
              orderDetails?.data
                ? parseOrderDetailsForModal(orderDetails.data)
                : []
            }
            isLoading={false}
            gridConfig={{ base: '1fr', md: 'repeat(2, 1fr)' }}
          />
        </Show>
      </FaceliftDialogWithExitConfirmation>
      <FaceliftInvoiceViewPDF
        viewInvoiceModal={viewInvoiceModal}
        isLoading={isFetchingPDF}
        pdfViewerUrl={pdfViewerUrl}
        isEmptyOrUnavailable={isEmptyOrUnavailable}
        isLoadError={isPdfLoadError}
        onOpenChange={() => {
          setViewInvoiceModal(!viewInvoiceModal);
          // Clear state when modal closes
          if (!viewInvoiceModal) {
            // Reset previous PDF
            resetPDFViewer();
          }
        }}
      />
    </Box>
  );
};

export default HomeDashboard;
